import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  Camera, Video, Star, Award, Users, Check, AlertCircle,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

// Calculate profile completeness and missing actions
export function calculateProfileCompleteness(profile) {
  if (!profile) return { score: 0, missing: [], completed: [] };
  
  const checks = [
    {
      id: "photo",
      label: "Add profile photo",
      icon: Camera,
      weight: 20,
      check: () => !!profile.portfolio?.profile_photo,
      priority: "high"
    },
    {
      id: "category",
      label: "Select primary category",
      icon: Star,
      weight: 15,
      check: () => !!profile.professional_info?.primary_category,
      priority: "high"
    },
    {
      id: "skills",
      label: "Add at least 3 skills",
      icon: Star,
      weight: 15,
      check: () => (profile.professional_info?.skills?.length || 0) >= 3,
      priority: "medium"
    },
    {
      id: "bio",
      label: "Write your bio",
      icon: Users,
      weight: 10,
      check: () => (profile.professional_info?.bio?.length || 0) > 50,
      priority: "medium"
    },
    {
      id: "showreel",
      label: "Add showreel/demo reel",
      icon: Video,
      weight: 15,
      check: () => !!profile.portfolio?.showreel_url || !!profile.portfolio?.intro_video,
      priority: "medium"
    },
    {
      id: "credit",
      label: "Add at least 1 credit",
      icon: Award,
      weight: 10,
      check: () => (profile.professional_info?.credits?.length || 0) >= 1,
      priority: "low"
    },
    {
      id: "award",
      label: "Add awards/achievements",
      icon: Award,
      weight: 10,
      check: () => (profile.professional_info?.awards?.length || 0) >= 1,
      priority: "low"
    },
    {
      id: "city",
      label: "Add your city",
      icon: Users,
      weight: 5,
      check: () => !!profile.personal_info?.city,
      priority: "low"
    }
  ];
  
  let totalWeight = 0;
  let earnedWeight = 0;
  const missing = [];
  const completed = [];
  
  for (const check of checks) {
    totalWeight += check.weight;
    if (check.check()) {
      earnedWeight += check.weight;
      completed.push(check);
    } else {
      missing.push(check);
    }
  }
  
  const score = Math.round((earnedWeight / totalWeight) * 100);
  
  // Sort missing by priority
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  missing.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  
  return { score, missing, completed };
}

export default function ProfileCompleteness({ profile }) {
  const navigate = useNavigate();
  const { score, missing, completed } = calculateProfileCompleteness(profile);
  
  // Determine color based on score
  let colorClass = "text-red-500";
  let bgClass = "bg-red-50";
  let progressClass = "bg-red-500";
  
  if (score >= 80) {
    colorClass = "text-green-600";
    bgClass = "bg-green-50";
    progressClass = "bg-green-500";
  } else if (score >= 50) {
    colorClass = "text-yellow-600";
    bgClass = "bg-yellow-50";
    progressClass = "bg-yellow-500";
  }
  
  // Show top 3 missing actions
  const topMissing = missing.slice(0, 3);
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Profile Completeness</CardTitle>
          <span className={`text-2xl font-bold ${colorClass}`}>{score}%</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Progress value={score} className={`h-2 ${progressClass}`} />
        
        {score < 100 && (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 font-medium">Complete your profile:</p>
            {topMissing.map((item) => {
              const Icon = item.icon;
              return (
                <div 
                  key={item.id}
                  className={`flex items-center gap-3 p-2 rounded-lg ${bgClass} cursor-pointer hover:opacity-80`}
                  onClick={() => navigate("/talent/wizard")}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-white`}>
                    <Icon className={`w-4 h-4 ${colorClass}`} />
                  </div>
                  <span className="text-sm flex-1">{item.label}</span>
                  <Badge variant="outline" className="text-xs">
                    {item.priority === "high" ? "+20%" : item.priority === "medium" ? "+10%" : "+5%"}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
        
        {score === 100 && (
          <div className="flex items-center gap-2 text-green-600">
            <Check className="w-5 h-5" />
            <span className="text-sm font-medium">Profile complete! Great job!</span>
          </div>
        )}
        
        {completed.length > 0 && score < 100 && (
          <div className="pt-2 border-t">
            <p className="text-xs text-gray-400 mb-1">Completed ({completed.length}/{completed.length + missing.length})</p>
            <div className="flex flex-wrap gap-1">
              {completed.slice(0, 4).map((item) => (
                <Badge key={item.id} variant="secondary" className="text-xs">
                  <Check className="w-3 h-3 mr-1" /> {item.label.split(" ").slice(0, 2).join(" ")}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full"
          onClick={() => navigate("/talent/wizard")}
        >
          Edit Profile <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}
